import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { OrganisationSettingsService } from '@/api';
import type { 
  CustomEnum, 
  CreateCustomEnumRequest, 
  UpdateCustomEnumRequest 
} from '@/types/organisation';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { accessRoleKeys } from './useAccessRoles';

interface UseCustomEnumsOptions {
  entityType?: string;
  enumField?: string;
}

export function useCustomEnums(options: UseCustomEnumsOptions = {}) {
  const { orgId, isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['custom-enums', orgId, options],
    queryFn: async () => {
      const response = await OrganisationSettingsService.getApiV1CustomEnums();
      let enums = response as CustomEnum[];
      
      // Filter by entity type if provided
      if (options.entityType) {
        enums = enums.filter(e => e.entity_type === options.entityType);
      }
      
      // Filter by enum field if provided
      if (options.enumField) {
        enums = enums.filter(e => e.enum_field === options.enumField);
      }
      
      return enums;
    },
    enabled: !!orgId && isSignedIn,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
}

export function useCreateCustomEnum() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (data: CreateCustomEnumRequest) => {
      const response = await OrganisationSettingsService.postApiV1CustomEnums(data);
      return response as CustomEnum;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-enums', orgId] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['effective-enums'] });
      // The Settings -> Access roles page lists the same employees.role rows.
      queryClient.invalidateQueries({ queryKey: accessRoleKeys.all });
      toast.success(t`Custom enum value created successfully`);
    },
    onError: () => {
      toast.error(t`Failed to create custom enum value`);
    },
  });
}

export function useUpdateCustomEnum() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async ({ 
      id, 
      data 
    }: { 
      id: string; 
      data: UpdateCustomEnumRequest 
    }) => {
      const response = await OrganisationSettingsService.putApiV1CustomEnums(id, data);
      return response as CustomEnum;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-enums', orgId] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['effective-enums'] });
      // The Settings -> Access roles page lists the same employees.role rows.
      queryClient.invalidateQueries({ queryKey: accessRoleKeys.all });
      toast.success(t`Custom enum value updated successfully`);
    },
    onError: () => {
      toast.error(t`Failed to update custom enum value`);
    },
  });
}

export function useDeleteCustomEnum() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async (enumId: string) => {
      await OrganisationSettingsService.deleteApiV1CustomEnums(enumId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-enums', orgId] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['effective-enums'] });
      // The Settings -> Access roles page lists the same employees.role rows.
      queryClient.invalidateQueries({ queryKey: accessRoleKeys.all });
      toast.success(t`Custom enum value deleted successfully`);
    },
    onError: () => {
      toast.error(t`Failed to delete custom enum value`);
    },
  });
}